import React, { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { useInput } from 'react-admin';
import { FormControl, FormHelperText, InputLabel } from '@mui/material';
import DOMPurify from 'dompurify';

// Custom Quill Editor Component
const QuillEditor = forwardRef(({ value, onChange, readOnly = false }, ref) => {
  const containerRef = useRef(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);

  useLayoutEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (ref) ref.current?.enable(!readOnly);
  }, [ref, readOnly]);

  useEffect(() => {
    const container = containerRef.current;
    const editorContainer = container.appendChild(
      container.ownerDocument.createElement('div')
    );
    
    const quill = new Quill(editorContainer, {
      theme: 'snow',
      readOnly,
      modules: {
        toolbar: [
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline'],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          ['blockquote'],
          [{ 'align': [] }],
          ['link'],
          ['clean']
        ],
      },
      formats: [
        'header', 'bold', 'italic', 'underline',
        'list', 'bullet', 'blockquote',
        'align', 'link'
      ]
    });

    if (ref) ref.current = quill;

    // Set initial value
    if (valueRef.current) {
      const isHTML = /^<([a-z]+)[^>]*>[\S\s]*<\/\1>$/.test(valueRef.current);
      if (isHTML) {
        quill.clipboard.dangerouslyPasteHTML(valueRef.current);
      } else {
        quill.setText(valueRef.current);
      }
    }

    quill.on(Quill.events.TEXT_CHANGE, () => {
      // Get HTML content and sanitize it
      const htmlContent = quill.root.innerHTML;
      const sanitizedContent = DOMPurify.sanitize(htmlContent, {
        ALLOWED_TAGS: [
          'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'span', 'div',
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'ul', 'ol', 'li',
          'blockquote', 'a'
        ],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
        FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button'],
        FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit']
      });
      
      onChangeRef.current?.(sanitizedContent);
    });

    return () => {
      if (ref) ref.current = null;
      container.innerHTML = '';
    };
  }, [ref, readOnly]);

  return <div ref={containerRef}></div>;
});

QuillEditor.displayName = 'QuillEditor';

const HtmlInput = ({ source, label, validate, helperText, ...props }) => {
  const {
    field,
    fieldState: { isTouched, invalid, error },
  } = useInput({
    source,
    validate,
    ...props,
  });

  const quillRef = useRef();

  // Handle content change
  const handleChange = (content) => {
    field.onChange(content);
  };

  // Update editor content when field value changes
  useEffect(() => {
    if (quillRef.current && field.value !== quillRef.current.root.innerHTML) {
      const currentContent = quillRef.current.root.innerHTML;
      const newContent = field.value || '';
      
      // Only update if content is actually different
      if (currentContent !== newContent) {
        if (newContent) {
          quillRef.current.clipboard.dangerouslyPasteHTML(newContent);
        } else {
          quillRef.current.setText('');
        }
      }
    }
  }, [field.value]);

  return (
    <FormControl fullWidth margin="normal" error={!!(isTouched && invalid)}>
      {label && <InputLabel shrink>{label}</InputLabel>}
      <div style={{ 
        marginTop: label ? '16px' : '0',
        border: isTouched && invalid ? '1px solid #d32f2f' : '1px solid #e0e0e0',
        borderRadius: '4px',
        backgroundColor: 'white'
      }}>
        <QuillEditor
          ref={quillRef}
          value={field.value || ''}
          onChange={handleChange}
          readOnly={false}
        />
      </div>
      {(helperText || (isTouched && invalid)) && (
        <FormHelperText>
          {isTouched && invalid ? error?.message : helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default HtmlInput; 